import { useState } from "react";
import { Checkbox, Radio } from "@mantine/core";
import { callFunction } from "../api/client";
import type { Campaign } from "../api/types";
import { WEB_COST, nameOf, stageWord } from "../lib/campaign";
import { Dialog } from "../ui/Dialog";

/* Moving a campaign to another stage, from its stage track: one sentence on
 * what the stage means, and for a move to Enriching, what starts with it.
 *
 * James, 2026-10-09: a move to Enriching says whether it starts a web run
 * and whether it goes back to the manual enrichment queue, and lets the
 * person choose, down to moving the stage and starting nothing. The web run
 * is ticked when the campaign looks on the web automatically, and the
 * request is not. The engine does the move and what is ticked in one
 * transaction (api.move_campaign, engine migration 209).
 *
 * Done is one step with two forms, Completed and Abandoned. A move to Done
 * asks which, and on a campaign already done the same dialog changes its
 * form (James, 2026-10-09).
 *
 * From Planning the dialog is Start enrichment or Mark ready to call, each
 * named for what it does (James, 2026-10-19). Start enrichment has the
 * campaign's saved choices ticked, Apollo among them, and the engine updates
 * the list and starts what is ticked (engine migration 256).
 *
 * A move to Calling or Done says what it does to the campaign behind in its
 * sequence, because the press starts that work (engine migration 314). */
const DONE_FORMS = [
  { to: "completed", label: "Completed", says: "The team worked the list through." },
  { to: "abandoned", label: "Abandoned", says: "The team stopped before the list was worked through." },
];

const SAYS: Record<string, string> = {
  draft: "It goes back to Planning. Start enrichment or Mark ready to call moves it on again.",
  enriching: "It goes back to enrichment. The team calls from it once it is moved to Calling.",
  ready_to_call: "Enrichment is done, and the list waits for the team.",
  calling: "The team is calling from this list. While it is, the enrichment tools are off.",
};

export function CampaignMove({ token, c, to, onClose, onMoved }: {
  token: string;
  c: Campaign;
  to: string;
  onClose: () => void;
  onMoved: () => void;
}) {
  const done = to === "done";
  const ended = c.stage === "completed" || c.stage === "abandoned";
  const planning = c.stage === "draft";
  const [form, setForm] = useState(DONE_FORMS.find((f) => f.to !== c.stage)?.to ?? "completed");
  const stage = done ? form : to;
  const [web, setWeb] = useState(Boolean(c.auto_enrich));
  const [manual, setManual] = useState(planning && Boolean(c.ask_enricher));
  const [apollo, setApollo] = useState(planning && Boolean(c.ask_apollo));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const enriching = to === "enriching";
  const requestOpen = c.manual_status === "requested" || c.manual_status === "in_progress";
  const records = c.web_candidates ? `the ${c.web_candidates.toLocaleString()} records` : "the records";
  const label = c.label ?? c.name;
  // From Planning, enrichment needs something to start, or it is Mark ready to call.
  const nothingTicked = planning && enriching && !web && !apollo && !(manual && !requestOpen);

  const move = async () => {
    setBusy(true); setError(null);
    try {
      await callFunction(token, "move_campaign", {
        p_name: c.name ?? "", p_stage: stage,
        p_look_on_web: enriching && web,
        p_request_manual: enriching && manual && !requestOpen,
        p_ask_apollo: enriching && apollo,
      });
      onMoved();
      onClose();
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const title = done ? (ended ? `Change how ${label} ended?` : `Move ${label} to Done?`)
    : planning && enriching ? `Start enrichment for ${label}?`
    : planning && to === "ready_to_call" ? `Mark ${label} ready to call?`
    : `Move ${label} to ${stageWord(to)}?`;
  const action = done ? `Mark ${stageWord(form).toLowerCase()}`
    : planning && enriching ? "Start enrichment"
    : planning && to === "ready_to_call" ? "Mark ready to call"
    : `Move to ${stageWord(to)}`;
  const says = planning && enriching ? "EVIE updates the list, then starts what is ticked."
    : planning && to === "ready_to_call" ? "Enrichment is skipped. The list is ready for the team."
    : SAYS[to];

  return (
    <Dialog opened title={title} onClose={onClose} busy={busy} error={error} disabled={nothingTicked}
            action={{ label: action, onPress: () => void move() }}>
      {done ? (
        <Radio.Group value={form} onChange={setForm} label="How did it end?">
          <div className="cm-options">
            {DONE_FORMS.map((f) => (
              <Radio key={f.to} value={f.to} disabled={f.to === c.stage}
                     label={f.to === c.stage ? `${f.label}, as it is now` : f.label} description={f.says} />
            ))}
          </div>
        </Radio.Group>
      ) : <p>{says}</p>}
      {/* What the move does to the campaign behind it in its sequence, in the
          engine's words (api.campaign, engine migration 314). */}
      {done && c.when_done_words ? <p className="muted cm-note">{c.when_done_words}</p> : null}
      {to === "calling" && c.when_calling_words ? <p className="muted cm-note">{c.when_calling_words}</p> : null}
      {enriching ? (
        <div className="cm-options">
          <Checkbox checked={web} onChange={(e) => setWeb(e.currentTarget.checked)}
                    label="Web enrichment" description={`Sends ${records} with gaps to web enrichment.`} />
          {web && c.web_needs_confirm ? <p className="cm-warn">{WEB_COST}</p> : null}
          <Checkbox checked={apollo} onChange={(e) => setApollo(e.currentTarget.checked)}
                    label="Apollo enrichment" description="Asks Apollo about the people to call who have no mobile." />
          {requestOpen ? (
            <p className="muted cm-note">
              Manual enrichment is already requested{c.manual_assigned_to ? `, with ${nameOf(c.manual_assigned_to)}` : ""}.
            </p>
          ) : (
            <Checkbox checked={manual} onChange={(e) => setManual(e.currentTarget.checked)}
                      label={planning ? "Manual enrichment" : "Request manual enrichment"} description="Adds it to the manual enrichment queue on the Enrichment page." />
          )}
          {nothingTicked ? <p className="muted cm-note">Tick at least one, or mark the campaign ready to call.</p> : null}
        </div>
      ) : null}
    </Dialog>
  );
}
